import * as React from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";
import { makeStyles } from "@fluentui/react-components";

import {
  DataGrid,
  DataGridHeader,
  DataGridHeaderCell,
  DataGridBody,
  DataGridRow,
  DataGridCell,
  TableCellLayout,
  createTableColumn,
} from "@fluentui/react-components";

// Components
import { ErrorMessagesBar } from "../../../shared/components/ErrorMessagesBar";
import CiTypeImage from "../../citypes/components/CiTypeImage";
import Spinner from "../../../shared/components/Spinner";

// Business logic
import { useSelectedCisContext } from "../context/SelectedCisContext";
import { useCisQuery } from "../hooks/useCiQueries";

const useStyles = makeStyles({
  container: {
    //height: "calc(100dvh - 200px)", // adjust based on layout
    display: "flex",
    flexDirection: "column",
    boxSizing: "border-box",
    height: "100%",
    overflowX: "auto",
    overflowY: "auto",
  },
  dataGrid: {
    height: "100%",
    minWidth: "fit-content",
  },
  stickyHeaderRow: {
    position: "sticky",
    top: 0,
    zIndex: 2,
    display: "flex",
  },
});

const CiDataGrid = () => {
  const { t } = useTranslation();
  const styles = useStyles();
  const { value: selectedCis, setValue: setSelectedCis } = useSelectedCisContext();
  const [errors, setErrors] = React.useState([]);

  // Handle URL search params and pass to Query
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const params = Object.fromEntries(queryParams.entries());

  const { data = [], isLoading, error } = useCisQuery(params);

  React.useEffect(() => {
    if (error) {
      setErrors([{ message: error?.message || t("generic.error"), type: "error", details: error }]);
    }
  }, [error, t]);

  if (isLoading) return <Spinner label={t("generic.loading")} />;

  const columns = [
    createTableColumn({
      columnId: "type",
      renderHeaderCell: () => t("generic.type"),
      renderCell: (item) => (
        <TableCellLayout>
          <span style={{ display: "inline-flex", alignItems: "center", gap: "8px" }}>
            <CiTypeImage
              ciType={{
                id: item.type_id,
                public_id: item.public_type_id,
                name: item.type,
                public_icon_id: item.public_icon_id,
              }}
              height={24}
            />
            {item.type}
          </span>
        </TableCellLayout>
      ),
    }),
    createTableColumn({
      columnId: "status",
      renderHeaderCell: () => t("generic.status"),
      renderCell: (item) => <TableCellLayout>{item.status}</TableCellLayout>,
    }),
    createTableColumn({
      columnId: "name",
      renderHeaderCell: () => t("generic.name"),
      renderCell: (item) => <TableCellLayout>{item.name}</TableCellLayout>,
    }),
    createTableColumn({
      columnId: "sn",
      renderHeaderCell: () => t("generic.serialNumber"),
      renderCell: (item) => <TableCellLayout>{item.sn}</TableCellLayout>,
    }),
    createTableColumn({
      columnId: "mn",
      renderHeaderCell: () => t("generic.modelNumber"),
      renderCell: (item) => <TableCellLayout>{item.mn}</TableCellLayout>,
    }),
    createTableColumn({
      columnId: "owner",
      renderHeaderCell: () => t("generic.owner"),
      renderCell: (item) => <TableCellLayout>{item.owner}</TableCellLayout>,
    }),
    createTableColumn({
      columnId: "site",
      renderHeaderCell: () => t("generic.site"),
      renderCell: (item) => <TableCellLayout>{item.site}</TableCellLayout>,
    }),
  ];

  const baseWidth = 100;
  const columnSizingOptions = {
    name: {
      idealWidth: baseWidth * 2,
      minWidth: baseWidth * 2,
    },
    email: {
      idealWidth: baseWidth * 2,
      minWidth: baseWidth * 2,
    },
  };

  const handleSelectionChange = (event, selectionData) => {
    const ids = [...selectionData.selectedItems];
    const newSelection = data.filter((item) => ids.includes(item.id));
    setSelectedCis(newSelection);
  };

  return (
    <div className={styles.container}>
      <ErrorMessagesBar errors={errors} onDismiss={() => setErrors([])} />
      <DataGrid
        items={data}
        columns={columns}
        columnSizingOptions={columnSizingOptions}
        selectedItems={selectedCis.map((item) => item.id)}
        getRowId={(item) => item.id}
        focusMode="composite"
        resizableColumns
        selectionMode="single"
        onSelectionChange={handleSelectionChange}
        className={styles.dataGrid}
      >
        <DataGridHeader>
          <DataGridRow
            className={styles.stickyHeaderRow}
            selectionCell={{
              checkboxIndicator: { "aria-label": "Select all rows" },
            }}
          >
            {({ renderHeaderCell }) => <DataGridHeaderCell>{renderHeaderCell()}</DataGridHeaderCell>}
          </DataGridRow>
        </DataGridHeader>

        <DataGridBody>
          {({ item, rowId }) => (
            <DataGridRow
              key={rowId}
              selectionCell={{
                checkboxIndicator: { "aria-label": "Select row" },
              }}
            >
              {({ renderCell }) => <DataGridCell>{renderCell(item)}</DataGridCell>}
            </DataGridRow>
          )}
        </DataGridBody>
      </DataGrid>
    </div>
  );
};

export default CiDataGrid;
